import { defineStore } from 'pinia'

type Messages = Record<string, string>
type LangMap  = Record<string, Messages>

export const useI18nStore = defineStore('i18n', {
  state: () => ({
    locale: 'ko' as string,
    messages: {} as LangMap,
    loaded:   {} as Record<string, boolean>,
  }),

  actions: {
    // ── DB API에서 언어 메시지를 직접 fetch 후 messages에 저장 ────────
    // _fallback=true 이면 catch 내 재귀 폴백을 건너뛰어 무한루프를 차단한다.
    async loadLocale(lang: string, _fallback = false): Promise<void> {
      if (this.loaded[lang]) {
        this.locale = lang
        return
      }
      try {
        // SSR 프로덕션(Docker)에서는 nitro.devProxy가 비활성이므로
        // runtimeConfig.apiBase(NUXT_API_BASE)를 명시적 baseURL로 사용한다.
        const config = useRuntimeConfig()
        const baseURL = import.meta.server
          ? (config.apiBase as string || config.public.apiBase)
          : ''
        // 클라이언트 측: 타임스탬프로 HTTP 캐시(CDN·브라우저·Nitro) 완전 우회
        const params: Record<string, string | number> = { lang }
        if (import.meta.client) params._t = Date.now()
        const data = await $fetch<Messages>(`/api/i18n/messages`, {
          baseURL: baseURL || undefined,
          params,
          // SSR 안전장치: 백엔드 주소가 잘못됐거나(예: 도커 네트워크 DNS 실패) 느릴 때
          // SSR 렌더가 수 초간 블로킹되는 것을 막는다. 실패 시 catch 의 폴백/키 노출로 처리.
          // 클라이언트는 별도 제한 없이 동작(앱 동작 후 갱신되므로 영향 적음).
          timeout: import.meta.server ? 1500 : undefined,
        })

        this.messages[lang] = data
        this.loaded[lang]   = true
        this.locale         = lang
      } catch (err) {
        console.error(`[i18n] 언어 로드 실패: ${lang}`, err)
        if (!_fallback) {
          if (lang !== 'en' && !this.loaded['en']) {
            await this.loadLocale('en', true)
          }
          if (lang !== 'ko' && !this.loaded['ko']) {
            await this.loadLocale('ko', true)
          }
        }
        this.locale = this.loaded[lang]
          ? lang
          : (this.loaded['en'] ? 'en' : 'ko')
      }
    },

    // ── 언어 변경 (로드 + localStorage 저장) ─────────────────
    async setLocale(lang: string): Promise<void> {
      await this.loadLocale(lang)
      if (import.meta.client) {
        localStorage.setItem('locale', lang)
      }
    },

    // ── 저장/수정/삭제 후 로드된 모든 언어를 강제 재fetch ──────
    // loaded 플래그를 초기화하면 loadLocale()이 캐시를 무시하고 DB에서 다시 읽는다.
    // API fetch 후 정적 JSON으로 gap-fill 하여 app:mounted 와 동일한 키 집합을 보장한다.
    async refreshAllLoaded(): Promise<void> {
      const langs = Object.keys(this.loaded).filter(k => this.loaded[k])
      langs.forEach(l => { this.loaded[l] = false })
      await Promise.all(langs.map(l => this.loadLocale(l)))
      // 클라이언트에서만: 정적 JSON(/locales/{lang}.json)으로 API 누락 키 보충
      // triggerExport() 직후 호출 시에는 정적 파일 = DB 데이터이므로 무해하다.
      if (import.meta.client) {
        await Promise.all(
          langs.map(async (l) => {
            try {
              const staticMsgs = await $fetch<Record<string, string>>(
                `/locales/${l}.json`,
                { params: { _t: Date.now() } }
              )
              if (this.messages[l]) {
                this.messages[l] = { ...staticMsgs, ...this.messages[l] }
              }
            } catch {}
          })
        )
      }
    },

    // ── 앱 초기화: localStorage → 저장된 언어 로드 ───────────
    async init(): Promise<void> {
      const saved = import.meta.client
        ? (localStorage.getItem('locale') ?? 'ko')
        : 'ko'
      await this.loadLocale(saved)
    },

    // ── 번역 함수 (폴백: 현재언어 → en → ko → key) ──────────
    // params 배열: t('KEY', [8])       → ${0} 를 8 로 치환
    // params 객체: t('KEY', {name:'홍'}) → ${name} 을 '홍' 으로 치환
    t(key: string, params?: Record<string, string | number> | (string | number)[]): string {
      // || 사용: null/undefined 뿐 아니라 빈 문자열('')도 폴백 처리
      const msg =
        this.messages[this.locale]?.[key] ||
        this.messages['en']?.[key] ||
        this.messages['ko']?.[key] ||
        key

      if (!params) return msg

      return msg.replace(/\$\{([^}]+)\}/g, (match, k: string) => {
        if (Array.isArray(params)) {
          const idx = Number(k.trim())
          return !Number.isNaN(idx) && idx < params.length
            ? String(params[idx])
            : match
        }
        const val = (params as Record<string, string | number>)[k.trim()]
        return val !== undefined ? String(val) : match
      })
    },
  },
})
